import { ID } from 'api'
import { Placement } from 'api/placement/types'
import { VirtualCurrency } from 'api/virtual-currency/types'
import { TagColor } from 'components/shared/Tag'
import {
  AddApplicationForm,
  Application,
  ApplicationStatus,
  SettingsForm,
} from './types'

interface StatusResult {
  text: string
  status: TagColor
}

export const mapAppStatus = (appStatus: ApplicationStatus) => {
  const textAndColor: StatusResult = {
    text: 'Unknown',
    status: 'default',
  }

  switch (appStatus) {
    case 'test':
      textAndColor.text = 'Testing'
      textAndColor.status = 'warning'
      break

    case 'enabled':
      textAndColor.text = 'Live'
      textAndColor.status = 'success'
      break

    case 'disabled':
      textAndColor.text = 'Disabled'
      break
  }

  return textAndColor
}

export function getEmptyAddApplication(): AddApplicationForm {
  return {
    clientSecurityToken: '',
    name: '',
    notInStore: false,
    platform: '',
    rewardCallbackToken: '',
    rewardCallbackUrl: '',
    status: 'disabled',
    storeUrl: '',

    placementAttributes: {
      exchangeRate: '',
      name: 'Default',
      status: 'disabled',
    },

    virtualCurrencyAttributes: {
      name: '',
    },

    vcIcon: null,
  }
}

type CreateVariables = Partial<Application> & {
  placementAttributes?: Partial<Placement>
  virtualCurrencyAttributes?: Partial<VirtualCurrency>
  file: File
}

export function getAddApplicationPayload(
  appData: AddApplicationForm,
  publisherId: ID
): CreateVariables {
  const { placementAttributes, virtualCurrencyAttributes, vcIcon, ...rest } =
    appData

  const payload = rest as CreateVariables

  payload['publisherId'] = publisherId

  // virtualCurrency
  payload['virtualCurrencyAttributes'] = virtualCurrencyAttributes
  payload['virtualCurrencyAttributes']['icon'] = vcIcon

  // placement
  payload['placementAttributes'] = placementAttributes as Partial<Placement>
  placementAttributes.exchangeRate = parseFloat(
    placementAttributes.exchangeRate as string
  )

  // exchange rate
  payload['exchangeRate'] = {
    rate: placementAttributes.exchangeRate,
    countries: ['WW'],
  }

  payload['plStatus'] = placementAttributes.status
  payload['plName'] = 'Default'
  payload['vcName'] = virtualCurrencyAttributes.name

  payload['status'] = rest.notInStore ? 'test' : 'enabled'

  if (rest.notInStore) delete payload['storeUrl']
  delete payload['placementAttributes']
  delete payload['virtualCurrencyAttributes']

  return payload
}

export function getEmptyApplication(): SettingsForm {
  return {
    useFyberVirtualCurrencyServer: true,
    clientSecurityToken: '',
    id: '',
    name: '',
    notInStore: false,
    storeRefresh: false,
    rewardCallbackToken: '',
    rewardCallbackUrl: '',
    status: 'disabled',
    storeUrl: '',
    apiFeedKey: '',
    apiFeedEnabled: true,
    sdkSecurityToken: '',
  }
}

export function getApplicationData(data: Application): SettingsForm {
  if (!data) {
    return getEmptyApplication()
  }

  return {
    useFyberVirtualCurrencyServer: !data.rewardCallbackUrl?.length,
    clientSecurityToken: data.clientSecurityToken ?? '',
    id: data.id ?? '',
    name: data.name ?? '',
    notInStore: data.notInStore ?? false,
    storeRefresh: false,
    rewardCallbackToken: data.rewardCallbackToken ?? '',
    rewardCallbackUrl: data.rewardCallbackUrl ?? '',
    status: data.status ?? 'disabled',
    storeUrl: data.storeUrl ?? '',
    apiFeedEnabled: data.apiFeedEnabled ?? false,
    apiFeedKey: data.apiFeedKey ?? '',
    rankingSourceAppId: data.rankingSourceAppId ?? '',
    sdkSecurityToken: data.sdkSecurityToken ?? '',
  }
}

export function getStoreName(platform?: string): string {
  if (platform === 'ios') return 'App Store'
  if (platform === 'android') return 'Google Play Store'
  return ''
}
